/**
 * Canonical URL utility for ricksblackcarservicesantonio.com
 * Enforces: HTTPS, no-www, no trailing slash (except /), lowercase, no utm/ref params
 */

const CANONICAL_HOST = 'ricksblackcarservicesantonio.com';
const CANONICAL_PROTOCOL = 'https';
const ALLOWED_PARAMS = new Set(['page']); // Whitelist query params

export interface CanonicalResult {
  canonical: string;
  needsRedirect: boolean;
}

/**
 * Normalize a URL to canonical form
 * @param url - Current URL (pathname + search + hash)
 * @param fullUrl - Optional full URL for absolute normalization
 */
export function getCanonicalUrl(url: string, fullUrl?: string): CanonicalResult {
  try {
    const base = `${CANONICAL_PROTOCOL}://${CANONICAL_HOST}`;
    const urlObj = fullUrl ? new URL(fullUrl) : new URL(url, base);
    
    const original = urlObj.toString();
    
    // Normalize protocol and host
    urlObj.protocol = `${CANONICAL_PROTOCOL}:`;
    urlObj.host = CANONICAL_HOST;
    
    // Normalize pathname
    let pathname = urlObj.pathname;
    
    // Remove index.html
    pathname = pathname.replace(/\/index\.html?$/i, '/');
    pathname = pathname.replace(/index\.html?$/i, '');
    
    // Lowercase
    pathname = pathname.toLowerCase();
    
    // Trailing slash policy: only root has trailing slash
    if (pathname !== '/') {
      // Remove trailing slash from non-root paths
      if (pathname.endsWith('/')) {
        pathname = pathname.slice(0, -1);
      }
      // Skip files with extensions
      const hasExtension = /\.[a-z0-9]+$/i.test(pathname);
      if (!hasExtension && !pathname.endsWith('/')) {
        // Already no trailing slash - good
      }
    }
    
    urlObj.pathname = pathname || '/';
    
    // Filter query params
    const filteredParams = new URLSearchParams();
    urlObj.searchParams.forEach((value, key) => {
      // Skip utm_* and ref params
      if (key.startsWith('utm_') || key === 'ref') {
        return;
      }
      // Keep only allowed params
      if (ALLOWED_PARAMS.has(key)) {
        filteredParams.append(key, value);
      }
    });
    
    urlObj.search = filteredParams.toString() ? `?${filteredParams.toString()}` : '';
    
    // Remove hash for canonical (hash is client-side only)
    urlObj.hash = '';
    
    const canonical = urlObj.toString();
    const needsRedirect = original !== canonical;
    
    return { canonical, needsRedirect };
  } catch (error) {
    console.error('Error normalizing URL:', error);
    return {
      canonical: `${CANONICAL_PROTOCOL}://${CANONICAL_HOST}${url}`,
      needsRedirect: false
    };
  }
}

/**
 * Get canonical pathname for use in SEO component
 */
export function getCanonicalPath(pathname: string): string {
  let path = pathname.toLowerCase();
  
  // Remove index.html
  path = path.replace(/\/index\.html?$/i, '/');
  path = path.replace(/index\.html?$/i, '');
  
  // Trailing slash: only root keeps it
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  
  return path || '/';
}
